import React, { useState } from 'react';
import { Box, makeStyles, IconButton, Collapse } from '@material-ui/core';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import { useTranslate } from 'react-admin';
import { FeatureCollection, PaginationInfo } from '../types';

interface MapDebugPanelProps {
    data: FeatureCollection;
    paginationInfo?: PaginationInfo | null;
}

const useStyles = makeStyles({
    container: {
        position: 'absolute',
        bottom: '8px',
        left: '8px',
        fontSize: '12px',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
        border: '1px solid rgba(0,0,0,0.1)',
        maxWidth: '200px',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        cursor: 'pointer',
        backgroundColor: 'rgba(0,0,0,0.02)',
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.05)',
        },
    },
    title: {
        fontWeight: 'bold',
        fontSize: '13px',
        color: '#333',
    },
    content: {
        padding: '12px',
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '4px',
        fontSize: '11px',
    },
    colorDot: {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        marginRight: '8px',
        border: '1px solid #fff',
        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
    },
    statsSection: {
        marginTop: '8px',
        paddingTop: '8px',
        borderTop: '1px solid rgba(0,0,0,0.1)',
    },
    toggleButton: {
        padding: '4px',
        '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.1)',
        },
    },
});

export const MapDebugPanel: React.FC<MapDebugPanelProps> = ({ data, paginationInfo }) => {
    const classes = useStyles();
    const t = useTranslate();
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <Box className={classes.container}>
            {/* Header with toggle button */}
            <Box className={classes.header} onClick={toggleExpanded}>
                <Box className={classes.title}>Légende de la carte</Box>
                <IconButton 
                    className={classes.toggleButton}
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded();
                    }}
                >
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
            </Box>

            {/* Collapsible content */}
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box className={classes.content}>
                    {/* Color Legend */}
                    <Box>
                        <Box className={classes.legendItem}>
                            <Box 
                                className={classes.colorDot} 
                                style={{ backgroundColor: '#ef2929' }}
                            />
                            <Box>Niveau 3 - Critique (Rouge)</Box>
                        </Box>
                        <Box className={classes.legendItem}>
                            <Box 
                                className={classes.colorDot} 
                                style={{ backgroundColor: '#ff9800' }}
                            />
                            <Box>Niveau 2 - Avertissement (Orange)</Box>
                        </Box>
                        <Box className={classes.legendItem}>
                            <Box 
                                className={classes.colorDot} 
                                style={{ backgroundColor: '#4caf50' }}
                            />
                            <Box>Niveau 1 - Normal (Vert)</Box>
                        </Box>
                        <Box className={classes.legendItem}>
                            <Box 
                                className={classes.colorDot} 
                                style={{ backgroundColor: '#9e9e9e' }}
                            />
                            <Box>Aucune donnée (Gris)</Box>
                        </Box>
                    </Box>

                    {/* Statistics */}
                    <Box className={classes.statsSection}>
                        <Box>Éléments: {data.features.length}</Box>
                        {paginationInfo?.actualApiItems && (
                            <Box>
                                Total API {t('app.common.items')}: {paginationInfo.actualApiItems}
                            </Box>
                        )}
                        {paginationInfo?.rejectedItems && paginationInfo.rejectedItems > 0 && (
                            <Box>
                                Rejetés: {paginationInfo.rejectedItems}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};
